/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps } from '../BaseColumn';
import { defaultValue } from './MultiSelectLOVColumn';

const MultiSelectLOVColumnManager: ComponentManagerFn<
  'MultiSelectLOVColumn'
> = (platform: Platform): ComponentManager<'MultiSelectLOVColumn'> => {
  return {
    ...baseComponentManager<'MultiSelectLOVColumn'>(
      platform,
      'MultiSelectLOVColumn',
    ),
    type: 'MultiSelectLOVColumn',
    name: 'Multi-Select LOV Column',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if ('ColumnFooter' === childType) {
        return true;
      }
      if (
        !(
          [
            'LOVGrid',
            'LOVMappings',
            'TriggerOnCellClick',
            'TriggerOnChange',
            'TriggerAfterServerValidate',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    defaultChildren: ['LOVGrid', 'LOVMappings'],
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }) => ['String'],
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['ReadOnlyDataStore'],
        required: true,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to prevent blank query on LOV open',
      },
      returnValueAttribute: {
        label: 'Return Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      returnDisplayAttribute: {
        label: 'Return Display Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      popupWidth: {
        label: 'Popup Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Popup Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      delimiter: {
        label: 'Delimeter',
        type: 'string',
        helpText:
          'Set a sequence of one or more characters to seperate the selected values. Default: ~',
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      showLovIcon: {
        label: 'Show LOV Icon',
        type: 'boolean',
        helpText:
          'When enabled, the LOV icon is always visible if the field is editable. When disabled, it appears only when the field is focused.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
      defaultFilterFocusColumn: {
        label: 'Default Filter Focus Column',
        type: 'ItemID',
        helpText:
          'Controls default filter focus. If a column Id is provided, the corresponding filter input will be focused when the popup opens.',
        initialFilter: 'LOVGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          const parentItem = platform.getItem(
            appUid,
            pageId,
            item?.parent || '',
          );

          if (parentItem?.type !== 'LOVGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      popupCssClassName: {
        label: 'Lov Popup CSS Class Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Look & Feel',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'returnValueAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'open':
          return false;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label?.value) ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-lov-field', iconType: 'cloudio' },
    excludeChildren: true,
    description: `**Overview**<br/>
The Multi-Select LOV Column is an advanced data entry component that enables users to select multiple records from a popup grid and store them in a single cell.
Similar to the standard LOV Column, it opens a modal window containing a Data Grid for searching and filtering. However, instead of picking just one item (e.g., "Assign Manager"), it allows users to pick several (e.g., "Assign Multiple Tags," "Select Team Members," or "Add Related Products") and saves the result as a delimited string.

**1. Data Storage Strategy**<br/>
Since databases typically store single values in cells, this component uses a Delimiter to pack multiple selections into one string field.
* **View Attribute:** The attribute in your main Data Store where the list of selected IDs will be saved.
* **Example:** If you select IDs 101, 102, and 103, and your delimiter is "|", the value stored in the database will be "101|102|103".
* **Delimiter:** The character used to separate values. (Default: "~" tilde). Common alternatives include comma (,), pipe (|), or semicolon (;). Ensure the delimiter you choose does not appear in the actual data values.

**2. Data Binding & Source**<br/>
You must define where the list of available options comes from.
* **Data Store Alias:** The external ReadOnly Data Store that populates the popup list.
* **Return Value Attribute:** The unique ID from the selected records to store (e.g., "UserID").
* **Return Display Attribute:** The human-readable text to display in the grid cell (e.g., "UserName").
* **Visual Output:** The grid cell will show a comma-separated list of names (e.g., "John Doe, Jane Smith") while the underlying data stores the IDs.

**3. Configuring the Popup**<br/>
Just like the single-select LOV, the popup content is fully customizable.
* **Child Component (LOV Grid):** Must contain an **LOV Grid** child to define the columns visible in the popup.
* **Dimensions:** Customize the **Popup Width / Height** (e.g., 800 x 600) to fit the data comfortably.
* **Show Grid Filters:** Enables the filter row in the popup for easy searching.

**4. Advanced Mappings**<br/>
* **Child Component (LOV Mappings):** Purpose: Map additional data from the selected records into other columns in the same row.
* **Behavior:** Since multiple items are selected, mapped values are typically also concatenated using the defined delimiter.

**5. Performance**<br/>
* **Do Not Prefetch:**
    * **Enabled:** Defers data loading until the user opens the popup. Essential for keeping the page fast if the LOV source contains thousands of records.`,
  };
};

export default MultiSelectLOVColumnManager;
